// Portfolio Gantt bar layout: per position around the bar, up to two core
// fields joined by a separator. Mirrors the backend's GanttBarLayoutRules; this
// release only edits and draws the right position with one field.
export const BAR_LABEL_FIELDS = Object.freeze(["ID", "INDEX", "NAME", "START", "FINISH", "PROGRESS"]);
export const BAR_LABEL_POSITIONS = Object.freeze(["left", "top", "right", "bottom"]);
export const BAR_LABEL_SEPARATORS = Object.freeze({
  DASH: " - ",
  SLASH: " / ",
  PIPE: " | ",
  DOT: " · ",
  ARROW: " → ",
  SPACE: " ",
});
export const DEFAULT_BAR_LABEL_FIELD = "NAME";
export const DEFAULT_BAR_LABEL_SEPARATOR = "DASH";
const MAX_FIELDS_PER_POSITION = 2;

const token = (raw) => (typeof raw === "string" ? raw.trim().toUpperCase() : raw);

const defaultLayout = () => ({
  right: { fields: [DEFAULT_BAR_LABEL_FIELD], separator: DEFAULT_BAR_LABEL_SEPARATOR },
});

// Unknown fields and separators are dropped; a layout with no usable position
// (older backend, broken value) reads as the default: the name on the right.
export const normalizeGanttBarLayout = (raw) => {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const layout = {};
  BAR_LABEL_POSITIONS.forEach((position) => {
    const slot = source[position];
    if (!slot || !Array.isArray(slot.fields)) return;
    const fields = slot.fields
      .map(token)
      .filter((field) => BAR_LABEL_FIELDS.includes(field))
      .slice(0, MAX_FIELDS_PER_POSITION);
    if (!fields.length) return;
    const separator = token(slot.separator);
    layout[position] = {
      fields,
      separator: BAR_LABEL_SEPARATORS[separator] ? separator : DEFAULT_BAR_LABEL_SEPARATOR,
    };
  });
  return Object.keys(layout).length ? layout : defaultLayout();
};

export const rightBarLabelField = (layout) =>
  normalizeGanttBarLayout(layout).right?.fields[0] ?? DEFAULT_BAR_LABEL_FIELD;

export const withRightBarLabelField = (layout, field) => {
  const current = normalizeGanttBarLayout(layout);
  return {
    ...current,
    right: {
      fields: [field],
      separator: current.right?.separator ?? DEFAULT_BAR_LABEL_SEPARATOR,
    },
  };
};

// Empty values are skipped so a missing field never leaves a dangling separator.
export const joinBarLabelValues = (values, separator) =>
  values
    .filter((value) => value !== "")
    .join(BAR_LABEL_SEPARATORS[separator] ?? BAR_LABEL_SEPARATORS[DEFAULT_BAR_LABEL_SEPARATOR]);

let layout = defaultLayout();
let layoutKey = JSON.stringify(layout);
const listeners = new Set();

// An unchanged layout notifies nobody, so a portfolio details refetch repaints no Gantt.
export const setGanttBarLayout = (details) => {
  const next = normalizeGanttBarLayout(details?.ganttBarLayout);
  const nextKey = JSON.stringify(next);
  if (nextKey === layoutKey) return;
  layout = next;
  layoutKey = nextKey;
  listeners.forEach((l) => l(layout));
};

export const getGanttBarLayout = () => layout;

export const onGanttBarLayoutChange = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
